import store from '../store.js'
import api from '../api.js'
import { getSpAccessToken, getGraphAccessToken, getSpGraphAccessToken } from '../../lib/msal.js'
import { ERROR_ACTION, showFeedback } from '../error/actions.js'
import {
  beginProgressDialog,
  updateProgressDialog,
  endProgressDialog
} from '../progress/actions.js'
import {
  closeOutlookWindow,
  emailFileNameOutlookComposeMode,
  getFileExtensionFromHost,
  isOffice,
  isWord,
  isOutlook,
  isOutlookComposeMode,
  openWordDocumentInHost,
  readCustomXml,
  readFileAttachments,
  readOfficeFile,
  readCurrentMailFile,
  readMailFile,
  readMailSyncState,
  readMailMetadata,
  saveDraftEmail,
  sendMail,
  setDocumentTitle,
  setPDSCategory,
  setDocumentProperty,
  setAutoOpenTaskPaneWithDocument,
  inspectSharePointConnections,
  writeCustomXml,
  resetCustomXml,
  getEmailMetadataReadMode,
  getEmailBodyComposeMode,
  setEmailSubjectComposeMode,
  getEmailSubjectComposeMode
} from '../../lib/office.js'
import {
  readSharePointFile,
  sharePointFileExistsInFolder,
  writeSharePointFile,
  overwriteSharePointFileByAbsoluteUrl,
  overwriteSharePointFileByEquivalentName,
  readSharePointFileAsPdf,
  writeSharePointFileNextToOriginal,
  updateListItemFields,
  updateEmailListItemFields
} from '../../lib/sharepoint'
import {
  stringToUint,
  cleanFileName,
  formatResourceString,
  openDocument,
  getFileExtension,
  PDF_CONVERTIBLE_FILES,
  getItemUrl,
  isMac,
  wait
} from '../../util.js'
import {
  xmlToObject,
  XML_DEFAULT_DATA
} from '../../lib/xml.js'
import config from '../../config.js'

export const SET_SAVE_DIALOG_FILENAME_ACTION = 'SET_SAVE_DIALOG_FILENAME_ACTION'
export const READ_DOCUMENT_PROPERTIES_ACTION = 'READ_DOCUMENT_PROPERTIES_ACTION'
export const SET_PROPERTY_ACTION = 'SET_PROPERTY_ACTION'
export const SET_SAVE_PDF_COPY_ACTION = 'SET_SAVE_PDF_COPY_ACTION'

const ERROR_TYPE_FILE_SAVE = 'ERROR_TYPE_FILE_SAVE'
const ERROR_TYPE_FILE_LOCKED = 'ERROR_TYPE_FILE_LOCKED'
const ERROR_TYPE_FILE_READ_ATTACHMENTS = 'ERROR_TYPE_FILE_READ_ATTACHMENTS'
const ERROR_TYPE_FILE_UPLOAD = 'ERROR_TYPE_FILE_UPLOAD'
const ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS = 'ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS'
const ERROR_TYPE_FILE_PDF_CONVERSION = 'ERROR_TYPE_FILE_PDF_CONVERSION'
const ERROR_TYPE_EMAIL_READ = 'ERROR_TYPE_EMAIL_READ'
const ERROR_TYPE_EMAIL_SAVE_DRAFT = 'ERROR_TYPE_EMAIL_SAVE_DRAFT'
const ERROR_TYPE_EMAIL_SEND = 'ERROR_TYPE_EMAIL_SEND'
const EMAIL_FILE_EXTENSIONS = ['eml', 'msg']

function isSharePointLockedError (err) {
  return err?.code === 'ITEM_LOCKED' || Number(err?.status) === 423
}

function resolveLockedFileErrorType (err, fallbackType) {
  return isSharePointLockedError(err) ? ERROR_TYPE_FILE_LOCKED : fallbackType
}

function resolveLockedFileErrorMessage (err) {
  if (!isSharePointLockedError(err)) {
    return err?.message
  }

  const { strings } = store.getState().locale || {}
  const get = (key, fallback) => {
    const value = strings?.[key]
    return typeof value === 'string' && value.length > 0 ? value : fallback
  }

  const genericHint = get(
    'FILE_LOCK_HINT_GENERIC',
    'The file is locked or currently in use. Close it and try again in a moment.'
  )

  const lockedBy = err?.lockDiagnostics?.lockedByUser
  if (lockedBy && (lockedBy.Email || lockedBy.Title)) {
    const who = lockedBy.Email || lockedBy.Title
    return formatResourceString(
      get(
        'FILE_LOCK_HINT_LOCKED_BY_USER',
        'Locked by %1. Ask that user to close the document, then try again.'
      ),
      who
    )
  }

  const checkedOutBy = err?.lockDiagnostics?.checkOutByUser
  if (checkedOutBy && (checkedOutBy.Email || checkedOutBy.Title)) {
    const who = checkedOutBy.Email || checkedOutBy.Title
    return formatResourceString(
      get(
        'FILE_LOCK_HINT_CHECKED_OUT_BY_USER',
        'Checked out by %1. Ask that user to check in or close the document, then try again.'
      ),
      who
    )
  }

  return genericHint
}

async function openPostSaveOfficeDocument (uri) {
  if (isWord() && config.postSaveTryWordHostHandoff !== false) {
    const didOpenInHost = await openWordDocumentInHost(uri)
    if (didOpenInHost) {
      console.info('save: completed word host-native handoff')
      return true
    }

    if (config.postSaveWordProtocolFallback === false) {
      console.info('save: word host handoff failed and protocol fallback is disabled')
      return false
    }
  }

  // Keep existing best-effort behavior for non-Word hosts.
  if (isMac()) {
    return openDocument(uri)
  }

  return openDocument(uri, null, {
    target: '_self'
  })
}

export function setSaveDialogFileName (fileName) {
  return {
    type: SET_SAVE_DIALOG_FILENAME_ACTION,
    fileName: cleanFileName(fileName)
  }
}

/**
 * Sets a property, but only for saving the state. It doesn't save
 * this property to the document.
 */
export function setProperty (key, value) {
  return {
    type: SET_PROPERTY_ACTION,
    key,
    value
  }
}

export function setSavePdfCopy (savePdfCopy) {
  return {
    type: SET_SAVE_PDF_COPY_ACTION,
    savePdfCopy: !!savePdfCopy
  }
}

/**
 * Async action for reading document properties.
 */
export function readDocumentProperties () {
  return async function (dispatch) {
    let xml

    try {
      xml = await readCustomXml()
      console.info('read custom xml', xml)
    } catch (e) {
      console.warn('failed to read custom xml', e)
      dispatch({
        type: READ_DOCUMENT_PROPERTIES_ACTION,
        documentProperties: {}
      })
      return
    }

    if (!xml) {
      console.info('no custom xml found, using default nil values')
      xml = XML_DEFAULT_DATA
    }
    dispatch({
      type: READ_DOCUMENT_PROPERTIES_ACTION,
      documentProperties: xmlToObject(xml)
    })
  }
}

/**
 * Async action for uploading a file to SharePoint.
 * siteItem: item picked in top tree, e.g. contact, project, intranet, department
 * fileItem: file node picked in the bottom tree
 * fileName: file name for this file
 * xmlProperties: properties to populate custom xml with
 * onSaveDone: call this function when save is done
 */
export function uploadFile (siteItem, fileItem, fileName, xmlProperties, savePdfCopy, onSaveDone, options = {}) {
  const state = store.getState()
  const userData = state.login.userData
  const showProgress = options?.showProgress !== false
  const overwrite = options?.overwrite !== false
  fileName = computeFileName(fileName)

  return async function (dispatch) {
    const dispatchSaveError = (err, fallbackType = ERROR_TYPE_FILE_SAVE) => {
      const baseMessage = resolveLockedFileErrorMessage(err)

      dispatch({
        type: ERROR_ACTION,
        errorType: resolveLockedFileErrorType(err, fallbackType),
        errorMessage: baseMessage
      })
    }

    let forceOverwrite = false
    const requestOverwrite = typeof options?.onOverwriteConflict === 'function'
      ? async (nextFileName) => {
        if (forceOverwrite) {
          return true
        }

        const shouldOverwrite = await options.onOverwriteConflict(nextFileName)
        if (shouldOverwrite) {
          forceOverwrite = true
        }
        return shouldOverwrite
      }
      : undefined

    try {
      // TODO figure out how to do this using jszip later, currently modifying
      // existing document!
      await setDocumentTitle(fileName.split('.')[0])
    } catch (e) {
      console.warn('failed to set document title', e)
    }

    const siteUrl = getItemUrl(siteItem)
    const path = fileItem.path
    let fileData = null
    let result = null

    const metadataFields = normalizeSharePointMetadataFields(xmlProperties)

    try {
      if (showProgress) {
        dispatch(
          beginProgressDialog(
            'PROGRESS_TITLE_UPLOAD_FILE',
            'PROGRESS_STATUS_READ_FILE'
          )
        )
      }

      const spToken = await getSpAccessToken(userData)

      if (isOffice()) {
        if (config.enablePostSaveAutoOpenTaskPane !== false) {
          try {
            await setAutoOpenTaskPaneWithDocument(true)
          } catch (e) {
            console.warn('failed to enable auto-open task pane setting', e)
          }
        }

        fileData = await readOfficeFile()
        // Read office file data into uint8array
        if (showProgress) {
          dispatch(updateProgressDialog('PROGRESS_STATUS_WRITE_CUSTOM_XML'))
        }
        // Set custom xml to default xml, if it exists
        fileData = await resetCustomXml(fileData)

        // Upload file to have SharePoint populate/add custom xml
        console.info('upload: saving document for custom xml', siteUrl, path, fileName)
        result = await writeSharePointFileWithOverwritePrompt({
          siteUrl,
          path,
          fileName,
          fileData,
          overwrite: forceOverwrite ? true : overwrite,
          spToken,
          onOverwriteConflict: requestOverwrite
        })
        // Give SharePoint some time to process the file
        await wait(config.waitForSaveTimeout)

        // Read the file data back from SharePoint.
        fileData = await readSharePointFile(result.absoluteUrl, spToken)

        // Diagnostic: log any xl/connections.xml injected by SharePoint.
        // Helps identify if SPO adds data connections that trigger Excel's
        // "External Data Connections have been disabled" security warning.
        inspectSharePointConnections(fileData)

        // Now we can populate the custom xml with the properties from the UI
        fileData = await writeCustomXml(fileData, xmlProperties)
      } else if (isOutlook()) {
        const graphToken = await getGraphAccessToken(userData)
        fileData = await readCurrentMailFile(graphToken)
      } else {
        console.info('not office and not outlook -> assuming browser')
        fileData = stringToUint('This is a test file')
      }

      console.info('upload: saving document', siteUrl, path, fileName)
      if (showProgress) {
        dispatch(updateProgressDialog('PROGRESS_STATUS_UPLOAD_FILE'))
      }

      if (isOffice()) {
        await overwriteSharePointFileByAbsoluteUrl({
          uri: result.absoluteUrl,
          fileData
        }, spToken)
      } else {
        result = await writeSharePointFileWithOverwritePrompt({
          siteUrl,
          path,
          fileName,
          fileData,
          overwrite: forceOverwrite ? true : overwrite,
          spToken,
          onOverwriteConflict: requestOverwrite
        })
      }

      let pdfCopyResult = null

      if (savePdfCopy && isSharePointPdfConvertible(fileName)) {
        if (showProgress) {
          dispatch(updateProgressDialog('PROGRESS_STATUS_UPLOAD_FILE'))
        }
        const spGraphToken = await getSpGraphAccessToken(userData)
        const pdfData = await readSharePointFileAsPdf(result.absoluteUrl, spGraphToken)
        const pdfFileName = getPdfCopyFileName(fileName)
        pdfCopyResult = await writeSharePointFileNextToOriginal({
          uri: result.absoluteUrl,
          fileName: pdfFileName,
          fileData: pdfData
        }, spToken)
        await waitForSharePointFileReadable(pdfCopyResult.absoluteUrl, spToken)
      }

      console.info('upload: file saved successfully')

      try {
        // Signal to close the currently opened document
        await setDocumentProperty('PDSCloseDocument', 1)
      } catch (e) {
        console.warn('failed to set document property', e)
      }

      if (isOffice()) {
        // Update metadata fields BEFORE opening the document so SharePoint
        // has the required properties set when Excel opens the file.
        // Opening first causes the "REQUIRED PROPERTIES" dialog on open.
        if (Object.keys(metadataFields).length > 0) {
          await updateListItemFields({
            listItemUrl: result.listItemUrl,
            fields: metadataFields
          }, spToken)

          if (pdfCopyResult?.listItemUrl) {
            await updateListItemFields({
              listItemUrl: pdfCopyResult.listItemUrl,
              fields: metadataFields
            }, spToken)
          }
        }

        const didOpenPostSave = await openPostSaveOfficeDocument(result.absoluteUrl)
        if (didOpenPostSave && showProgress) {
          dispatch(updateProgressDialog('PROGRESS_STATUS_OPEN_FILE'))
        }
      } else if (isOutlook()) {
        setPDSCategory()
        await updateEmailListItemFields({
          siteUrl,
          listItemUrl: result.listItemUrl,
          fields: getEmailMetadataReadMode(),
          additionalFields: metadataFields
        }, spToken)

        if (pdfCopyResult?.listItemUrl) {
          await updateEmailListItemFields({
            siteUrl,
            listItemUrl: pdfCopyResult.listItemUrl,
            fields: getEmailMetadataReadMode(),
            additionalFields: metadataFields
          }, spToken)
        }
      }

      const meta = {
        type: siteItem.type,
        id: siteItem.id,
        path,
        fileName,
        url: encodeURI(`${siteUrl}/${path}/${fileName}`)
      }
      try {
        await api.addFileSavedEvent(meta)
      } catch (e) {
        console.warn('failed to add file saved event', e)
      }

      if (typeof onSaveDone === 'function') {
        onSaveDone()
      }

      if (showProgress) {
        setTimeout(() => dispatch(endProgressDialog()), 1000)
      }
    } catch (e) {
      if (e?.code === 'USER_ABORTED_OVERWRITE') {
        if (showProgress) {
          dispatch(endProgressDialog())
        }
        return
      }

      if (Number(e?.status) === 406) {
        dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_FILE_PDF_CONVERSION
        })
        return
      }
      dispatchSaveError(e)
    }
  }
}

/**
 * Called from read mode in outlook when saving attachments.
 * siteItem: item picked in top tree, e.g. contact, project, intranet, department
 * fileItem: file node picked in the bottom tree
 * onSaveDone: call this function when save is done
 */
export function uploadFileAttachments (siteItem, fileItem, onSaveDone) {
  const state = store.getState()
  const userData = state.login.userData

  return function (dispatch) {
    dispatch(
      beginProgressDialog(
        'PROGRESS_TITLE_UPLOAD_ATTACHMENTS',
        'PROGRESS_STATUS_READ_ATTACHMENTS'
      )
    )

    readFileAttachments(async (err, attachments) => {
      if (err) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_FILE_READ_ATTACHMENTS,
          errorMessage: err.message
        })
      }

      await uploadAttachments({
        dispatch,
        userData,
        siteItem,
        fileItem,
        attachments,
        onSaveDone
      })
    })
  }
}

/**
 * Saves email attachments with user-updated names.
 */
export function uploadRenamedFileAttachments (siteItem, fileItem, renamedAttachments, onSaveDone) {
  const state = store.getState()
  const userData = state.login.userData

  return async function (dispatch) {
    dispatch(
      beginProgressDialog(
        'PROGRESS_TITLE_UPLOAD_ATTACHMENTS',
        'PROGRESS_STATUS_READ_ATTACHMENTS'
      )
    )

    const attachments = Array.isArray(renamedAttachments)
      ? renamedAttachments.map(attachment => {
        return {
          ...attachment,
          fileName: cleanFileName(attachment.fileName)
        }
      })
      : []

    await uploadAttachments({
      dispatch,
      userData,
      siteItem,
      fileItem,
      attachments,
      onSaveDone
    })
  }
}

export function uploadDroppedFiles (siteItem, fileItem, droppedFiles, metadataFields, onSaveDone, options = {}) {
  const state = store.getState()
  const userData = state.login.userData
  const showProgress = options?.showProgress !== false
  const defaultOverwrite = options?.overwrite !== false

  return async function (dispatch) {
    const files = Array.isArray(droppedFiles)
      ? droppedFiles.map((entry) => {
        if (entry && typeof entry.arrayBuffer === 'function') {
          return {
            file: entry,
            fileName: String(entry.name || ''),
            isUserProvidedName: false,
            overwrite: defaultOverwrite
          }
        }

        if (entry?.file && typeof entry.file.arrayBuffer === 'function') {
          return {
            file: entry.file,
            fileName: String(entry.fileName || entry.file.name || ''),
            isUserProvidedName: true,
            overwrite: typeof entry.overwrite === 'boolean' ? entry.overwrite : defaultOverwrite
          }
        }

        return null
      }).filter((entry) => entry && typeof entry.fileName === 'string' && entry.fileName.length > 0)
      : []

    if (files.length === 0) {
      return
    }

    let spToken
    let siteUrl
    let path

    if (showProgress) {
      dispatch(
        beginProgressDialog(
          'PROGRESS_TITLE_UPLOAD_ATTACHMENTS',
          'PROGRESS_STATUS_UPLOAD_ATTACHMENTS',
          cleanFileName(files[0].fileName)
        )
      )
    }

    try {
      spToken = await getSpAccessToken(userData)
      siteUrl = getItemUrl(siteItem)
      path = fileItem.path
    } catch (err) {
      return dispatch({
        type: ERROR_ACTION,
        errorType: resolveLockedFileErrorType(err, ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS),
        errorMessage: resolveLockedFileErrorMessage(err)
      })
    }

    try {
      const normalizedMetadataFields = normalizeSharePointMetadataFields(metadataFields)
      const emailMetadata = isOutlook() && !isOutlookComposeMode()
        ? getEmailMetadataReadMode()
        : null

      for (const fileEntry of files) {
        const fileBuffer = await readDroppedFileArrayBuffer(fileEntry.file)
        const droppedMailDate = isDroppedEmailFile(fileEntry, fileEntry.fileName)
          ? resolveDroppedEmailDate({
            fileName: fileEntry.fileName,
            fileBuffer,
            fallbackDateTime: emailMetadata?.dateTimeCreated,
            file: fileEntry.file
          })
          : null

        const fileName = cleanFileName(
          resolveDroppedUploadFileName(fileEntry, droppedMailDate)
        )

        if (showProgress) {
          dispatch(updateProgressDialog(
            'PROGRESS_STATUS_UPLOAD_ATTACHMENTS',
            fileName
          ))
        }

        const result = await writeSharePointFileWithOverwritePrompt({
          siteUrl,
          path,
          fileName,
          fileData: new Uint8Array(fileBuffer),
          overwrite: fileEntry.overwrite !== false,
          spToken,
          onOverwriteConflict: options?.onOverwriteConflict
        })

        if (result?.listItemUrl) {
          if (emailMetadata && isDroppedEmailFile(fileEntry, fileName)) {
            const ext = getFileExtension(fileName)
            const mime = String(fileEntry?.file?.type || '').toLowerCase()
            const resolvedEmailMetadata = (ext === 'eml' || mime.startsWith('message/rfc822'))
              ? withDroppedEmailSenderFallback(emailMetadata, fileBuffer)
              : emailMetadata
            await updateEmailListItemFields({
              siteUrl,
              listItemUrl: result.listItemUrl,
              fields: withDroppedEmailDateTime(resolvedEmailMetadata, droppedMailDate),
              additionalFields: normalizedMetadataFields
            }, spToken)
          } else if (Object.keys(normalizedMetadataFields).length > 0) {
            await updateListItemFields({
              listItemUrl: result.listItemUrl,
              fields: normalizedMetadataFields
            }, spToken)
          }
        }

        const meta = {
          type: siteItem.type,
          id: siteItem.id,
          path,
          fileName,
          url: encodeURI(`${siteUrl}/${path}/${fileName}`)
        }
        await api.addFileSavedEvent(meta)
      }
    } catch (err) {
      if (err?.code === 'USER_ABORTED_OVERWRITE') {
        if (showProgress) {
          dispatch(endProgressDialog())
        }
        return
      }

      return dispatch({
        type: ERROR_ACTION,
        errorType: ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS,
        errorMessage: err.message
      })
    }

    if (typeof onSaveDone === 'function') {
      onSaveDone()
    }

    if (showProgress) {
      dispatch(endProgressDialog())
    }
  }
}

export function targetFileExistsInFolder (siteItem, fileItem, fileName) {
  const state = store.getState()
  const userData = state.login.userData

  return async function () {
    const spToken = await getSpAccessToken(userData)
    const siteUrl = getItemUrl(siteItem)
    const folderPath = fileItem.path
    return sharePointFileExistsInFolder({
      siteUrl,
      folderPath,
      fileName: cleanFileName(fileName)
    }, spToken)
  }
}

function isDroppedEmailFile (fileEntry, fileName) {
  const ext = getFileExtension(String(fileName || ''))
  if (EMAIL_FILE_EXTENSIONS.includes(ext)) {
    return true
  }

  const mime = String(fileEntry?.file?.type || '').toLowerCase()
  return mime.startsWith('message/')
}

function resolveDroppedUploadFileName (fileEntry, droppedMailDate) {
  if (fileEntry?.isUserProvidedName) {
    return String(fileEntry?.fileName || '')
  }

  return getDroppedUploadFileName(fileEntry?.file, droppedMailDate)
}

function getDroppedUploadFileName (file, droppedMailDate) {
  const trimmed = String(file?.name || '').trim()
  if (!trimmed.length) {
    return ''
  }

  const ext = getFileExtension(trimmed)
  if (!EMAIL_FILE_EXTENSIONS.includes(ext)) {
    return trimmed
  }

  const suffix = `.${ext}`
  const baseName = trimmed.toLowerCase().endsWith(suffix)
    ? trimmed.slice(0, -suffix.length)
    : trimmed
  const timestamp = formatDateForFileName(droppedMailDate || resolveDroppedFileDate(file))
  return `${baseName} ${timestamp}.${ext}`
}

function formatDateForFileName (date) {
  const resolved = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date()
  const yyyy = resolved.getFullYear()
  const mm = String(resolved.getMonth() + 1).padStart(2, '0')
  const dd = String(resolved.getDate()).padStart(2, '0')
  const hh = String(resolved.getHours()).padStart(2, '0')
  const min = String(resolved.getMinutes()).padStart(2, '0')
  const ss = String(resolved.getSeconds()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}-${min}-${ss}`
}

function formatDateForEmailMetadata (date) {
  const resolved = date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date()
  const yyyy = resolved.getFullYear()
  const mm = String(resolved.getMonth() + 1).padStart(2, '0')
  const dd = String(resolved.getDate()).padStart(2, '0')
  const hh = String(resolved.getHours()).padStart(2, '0')
  const min = String(resolved.getMinutes()).padStart(2, '0')
  const ss = String(resolved.getSeconds()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`
}

function resolveDroppedFileDate (file) {
  const millis = Number(file?.lastModified)
  if (Number.isFinite(millis) && millis > 0) {
    return new Date(millis)
  }

  if (file?.lastModifiedDate instanceof Date && !Number.isNaN(file.lastModifiedDate.getTime())) {
    return file.lastModifiedDate
  }

  return new Date()
}

function withDroppedEmailDateTime (emailMetadata, droppedDate) {
  const normalizedDate = formatDateForEmailMetadata(droppedDate || parseDateLike(emailMetadata?.dateTimeCreated) || new Date())
  return {
    ...emailMetadata,
    dateTimeCreated: normalizedDate,
    documentDate: normalizedDate
  }
}

function withDroppedEmailSenderFallback (emailMetadata, fileBuffer) {
  const sender = String(emailMetadata?.sender || '').trim()
  if (sender.length > 0) {
    return emailMetadata
  }

  const senderFromEml = extractEmlSenderAddress(fileBuffer)
  if (senderFromEml.length > 0) {
    return {
      ...emailMetadata,
      sender: senderFromEml
    }
  }

  return emailMetadata
}

function resolveDroppedEmailDate ({ fileName, fileBuffer, fallbackDateTime, file }) {
  const ext = getFileExtension(String(fileName || ''))
  const mime = String(file?.type || '').toLowerCase()
  const shouldParseAsEml = ext === 'eml' || mime.startsWith('message/rfc822')

  if (shouldParseAsEml) {
    const fromEml = extractEmlDate(fileBuffer)
    if (fromEml) {
      return fromEml
    }
  }

  return parseDateLike(fallbackDateTime) || resolveDroppedFileDate(file)
}

function extractEmlDate (fileBuffer) {
  try {
    const lines = getEmlHeaderLines(fileBuffer)

    for (const line of lines) {
      if (!/^received\s*:/i.test(line)) {
        continue
      }
      const value = line.slice(line.indexOf(':') + 1)
      const fromSemicolon = value.includes(';') ? value.slice(value.lastIndexOf(';') + 1).trim() : ''
      const parsedReceived = parseDateLike(fromSemicolon)
      if (parsedReceived) {
        return parsedReceived
      }
    }

    const dateLine = lines.find((line) => /^date\s*:/i.test(line))
    if (dateLine) {
      return parseDateLike(dateLine.slice(dateLine.indexOf(':') + 1).trim())
    }

    return null
  } catch (err) {
    return null
  }
}

function extractEmlSenderAddress (fileBuffer) {
  const fromHeader = extractEmlHeaderValue(fileBuffer, 'from')
  if (!fromHeader) {
    return ''
  }

  const bracketMatch = fromHeader.match(/<([^>]+)>/)
  const candidate = bracketMatch ? bracketMatch[1] : fromHeader
  const cleaned = String(candidate).replace(/^"|"$/g, '').trim()
  return /@/.test(cleaned) ? cleaned : ''
}

function extractEmlHeaderValue (fileBuffer, headerName) {
  const lines = getEmlHeaderLines(fileBuffer)
  const prefix = `${String(headerName || '').toLowerCase()}:`
  const line = lines.find((entry) => entry.toLowerCase().startsWith(prefix))
  if (!line) {
    return ''
  }
  return line.slice(line.indexOf(':') + 1).trim()
}

function getEmlHeaderLines (fileBuffer) {
  const bytes = fileBuffer instanceof ArrayBuffer
    ? new Uint8Array(fileBuffer)
    : (fileBuffer?.buffer instanceof ArrayBuffer ? new Uint8Array(fileBuffer.buffer) : null)

  if (!bytes || bytes.length === 0) {
    return []
  }

  const maxBytes = Math.min(bytes.length, 256 * 1024)
  const headerChunk = bytes.slice(0, maxBytes)
  const text = new TextDecoder('utf-8', { fatal: false }).decode(headerChunk)
  const separatorMatch = text.match(/\r?\n\r?\n/)
  const headerText = separatorMatch ? text.slice(0, separatorMatch.index) : text
  const unfoldedHeaders = headerText.replace(/\r?\n[ \t]+/g, ' ')
  return unfoldedHeaders.split(/\r?\n/)
}

function parseDateLike (value) {
  if (!value) {
    return null
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value
  }

  const text = String(value).trim()
  const isoCandidate = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(text)
    ? text.replace(' ', 'T')
    : text

  const parsed = new Date(isoCandidate)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return parsed
}

async function uploadAttachments ({ dispatch, userData, siteItem, fileItem, attachments, onSaveDone }) {
  let spToken
  let siteUrl
  let path

  try {
    spToken = await getSpAccessToken(userData)
    siteUrl = getItemUrl(siteItem)
    path = fileItem.path
  } catch (err) {
    return dispatch({
      type: ERROR_ACTION,
      errorType: ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS,
      errorMessage: err.message
    })
  }

  try {
    for (const attachment of attachments) {
      const { fileData } = attachment
      const fileName = cleanFileName(attachment.fileName)
      console.info('upload: saving attachment', siteUrl, path, fileName)
      dispatch(updateProgressDialog(
        'PROGRESS_STATUS_UPLOAD_ATTACHMENTS',
        fileName
      ))
      const result = await writeSharePointFile({
        siteUrl,
        path,
        fileName,
        fileData
      }, spToken)

      const metadataFields = normalizeSharePointMetadataFields(attachment.metadataFields)
      if (result?.listItemUrl && Object.keys(metadataFields).length > 0) {
        await updateListItemFields({
          listItemUrl: result.listItemUrl,
          fields: metadataFields
        }, spToken)
      }
    }
  } catch (err) {
    console.error('failed to write attachment to sharepoint', err)
    return dispatch({
      type: ERROR_ACTION,
      errorType: resolveLockedFileErrorType(err, ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS),
      errorMessage: resolveLockedFileErrorMessage(err)
    })
  }

  if (typeof onSaveDone === 'function') {
    onSaveDone()
  }

  try {
    for (const attachment of attachments) {
      const { fileName } = attachment
      const meta = {
        type: siteItem.type,
        id: siteItem.id,
        path,
        fileName,
        url: encodeURI(`${siteUrl}/${path}/${fileName}`)
      }
      await api.addFileSavedEvent(meta)
    }
  } catch (err) {
    console.error('failed to add file save event', err)
    return dispatch({
      type: ERROR_ACTION,
      errorType: ERROR_TYPE_FILE_UPLOAD_ATTACHMENTS,
      errorMessage: err.message
    })
  }

  console.info('Successfully uploaded attachments')
  dispatch(endProgressDialog())
}

/**
 * Called from compose mode in outlook.
 *
 * Saves the email -> So the email will get an itemId
 * Reads back the email data (retries until success)
 * Sends the email using the Graph API
 * Files the email in the selected folder
 * Closes the mail window (doesn't work when in inline mode)
 */
export function sendAndFile (siteItem, fileItem) {
  const state = store.getState()
  const userData = state.login.userData

  // TODO we need some sort of progress dialog here
  // since the user doesn't know anything of what is going on

  const flowStartedAt = Date.now()

  function toTimestamp (value) {
    const ts = Date.parse(String(value || ''))
    return Number.isFinite(ts) ? ts : 0
  }

  function normalizeBodyForCompare (value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
  }

  function isGraphBodyConsistent ({ composeBody, graphBody }) {
    const compose = normalizeBodyForCompare(composeBody)
    const graph = normalizeBodyForCompare(graphBody)

    if (!compose.length) {
      return true
    }

    if (!graph.length) {
      return false
    }

    if (compose === graph || graph.includes(compose)) {
      return true
    }

    const head = compose.slice(0, Math.min(80, compose.length))
    const tail = compose.slice(-Math.min(120, compose.length))
    if (head.length >= 32 && tail.length >= 32) {
      return graph.includes(head) && graph.includes(tail)
    }

    return false
  }

  function normalizeRetrySchedule (retrySchedule = []) {
    if (!Array.isArray(retrySchedule)) {
      return []
    }
    return retrySchedule
      .map((value) => Math.max(0, Number(value) || 0))
      .filter((value) => Number.isFinite(value))
  }

  async function waitForGraphFreshDraft ({ itemId, graphToken, baselineState, retrySchedule = [] }) {
    const delays = normalizeRetrySchedule(retrySchedule)
    const attempts = delays.length + 1
    let latestState = null

    for (let attempt = 1; attempt <= attempts; attempt++) {
      if (attempt > 1) {
        await wait(delays[attempt - 2])
      }

      latestState = await readMailSyncState(itemId, graphToken)

      const changedKey = baselineState?.changeKey &&
        latestState?.changeKey &&
        baselineState.changeKey !== latestState.changeKey

      const newerTimestamp = toTimestamp(latestState?.lastModifiedDateTime) >
        toTimestamp(baselineState?.lastModifiedDateTime)

      const hasAnySyncState = !!latestState?.changeKey || !!latestState?.lastModifiedDateTime

      if (!baselineState ? hasAnySyncState : (changedKey || newerTimestamp)) {
        return {
          confirmedFresh: true,
          attempts: attempt,
          state: latestState
        }
      }
    }

    return {
      confirmedFresh: false,
      attempts,
      state: latestState
    }
  }

  async function waitForGraphBodyConsistency ({ itemId, graphToken, composeBodyText, retrySchedule = [] }) {
    const delays = normalizeRetrySchedule(retrySchedule)
    const attempts = delays.length + 1

    for (let attempt = 1; attempt <= attempts; attempt++) {
      if (attempt > 1) {
        await wait(delays[attempt - 2])
      }

      const syncState = await readMailSyncState(itemId, graphToken, { includeBody: true })
      const consistent = isGraphBodyConsistent({
        composeBody: composeBodyText,
        graphBody: syncState?.bodyText
      })

      if (consistent) {
        return {
          consistent: true,
          attempts: attempt,
          state: syncState
        }
      }
    }

    return {
      consistent: false,
      attempts,
      state: null
    }
  }

  async function readEmailWithRetries (itemId) {
    let retries = config.outlook.readRetryCount
    const retryTimeout = config.outlook.readRetryTimeout

    do {
      try {
        const graphToken = await getGraphAccessToken(userData)
        const fileData = await readMailFile(itemId, graphToken)
        console.info('Read draft email successfully')
        return fileData
      } catch (err) {
        if (--retries > 0) {
          console.info('Failed to read draft email,', retries, 'retries left')
          await wait(retryTimeout)
        } else {
          console.error('Failed to read draft email', err)
          throw err
        }
      }
    } while (true)
  }

  return async function (dispatch) {
    if (!isOutlookComposeMode()) {
      console.warn('Can only call sendAndFile() in outlook compose mode -> no op')
      return
    }

    if (siteItem.type === 'project') {
      let subject = await getEmailSubjectComposeMode()
      if (!subject.match(`(pds-p${siteItem.id})`)) {
        subject = subject + ` (pds-p${siteItem.id})`
        await setEmailSubjectComposeMode(subject)
      }
    }

    dispatch(beginProgressDialog(
      'PROGRESS_TITLE_SEND_AND_FILE',
      'PROGRESS_STATUS_SAVE_DRAFT_EMAIL'
    ))

    setPDSCategory()

    dispatch(updateProgressDialog(
      'PROGRESS_STATUS_SET_CATEGORY_DRAFT_EMAIL'
    ))

    const existingItemId = String(Office?.context?.mailbox?.item?.itemId || '')
    let graphToken = null
    let baselineGraphState = null

    if (existingItemId.length > 0) {
      try {
        graphToken = await getGraphAccessToken(userData)
        baselineGraphState = await readMailSyncState(existingItemId, graphToken)
      } catch (err) {
        console.warn('send and file telemetry: failed to read baseline graph draft state', err)
      }
    }

    let itemId = null
    try {
      let retries = 5
      do {
        itemId = await saveDraftEmail()
        if (typeof itemId === 'string' && itemId.length > 0) {
          console.info('Got draft mail id', itemId)
          break
        } else if (--retries > 0) {
          console.warn('Retrying save draft email..')
          await wait(2000)
        } else {
          throw new Error('Failed to save draft email')
        }
      } while (true)
    } catch (err) {
      return dispatch({
        type: ERROR_ACTION,
        errorType: ERROR_TYPE_EMAIL_SAVE_DRAFT,
        errorMessage: err.message
      })
    }

    dispatch(updateProgressDialog(
      'PROGRESS_STATUS_FETCH_EMAIL_WITH_RETRIES'
    ))
    let fileData = null
    try {
      fileData = await readEmailWithRetries(itemId)
    } catch (err) {
      return dispatch({
        type: ERROR_ACTION,
        errorType: ERROR_TYPE_EMAIL_READ,
        errorMessage: err.message
      })
    }

    console.info('send and file telemetry: draft saved', {
      itemId,
      elapsedMs: Date.now() - flowStartedAt,
      baselineChangeKey: baselineGraphState?.changeKey || '',
      baselineLastModifiedDateTime: baselineGraphState?.lastModifiedDateTime || ''
    })

    try {
      const guardRetrySchedule = [250, 700, 1600, 3200, 6400]

      if (!graphToken) {
        graphToken = await getGraphAccessToken(userData)
      }

      const freshness = await waitForGraphFreshDraft({
        itemId,
        graphToken,
        baselineState: baselineGraphState,
        retrySchedule: guardRetrySchedule
      })

      console.info('send and file telemetry: graph freshness', {
        itemId,
        freshnessConfirmed: freshness.confirmedFresh,
        freshnessAttempts: freshness.attempts,
        latestChangeKey: freshness.state?.changeKey || '',
        latestLastModifiedDateTime: freshness.state?.lastModifiedDateTime || '',
        elapsedMs: Date.now() - flowStartedAt
      })

      if (!freshness.confirmedFresh) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_EMAIL_READ,
          errorMessage: 'E_SEND_AND_FILE_STALE_DRAFT_VERSION'
        })
      }

      const composeBodyText = await getEmailBodyComposeMode()
      const bodyCheck = await waitForGraphBodyConsistency({
        itemId,
        graphToken,
        composeBodyText,
        retrySchedule: guardRetrySchedule
      })

      console.info('send and file telemetry: body consistency', {
        itemId,
        bodyConsistent: bodyCheck.consistent,
        attempts: bodyCheck.attempts,
        composeBodyLength: String(composeBodyText || '').length,
        graphBodyLength: String(bodyCheck.state?.bodyText || '').length,
        latestChangeKey: bodyCheck.state?.changeKey || '',
        latestLastModifiedDateTime: bodyCheck.state?.lastModifiedDateTime || '',
        elapsedMs: Date.now() - flowStartedAt
      })

      if (!bodyCheck.consistent) {
        // Avoid false-positive hard failures by forcing one more draft save
        // and MIME refresh before sending.
        console.warn('send and file: body consistency guard mismatch, applying fallback refresh')
        await saveDraftEmail()
        fileData = await readMailFile(itemId, graphToken)
        console.info('send and file telemetry: fallback refresh complete', {
          itemId,
          refreshedMimeBytes: fileData.length,
          elapsedMs: Date.now() - flowStartedAt
        })
      }
    } catch (err) {
      return dispatch({
        type: ERROR_ACTION,
        errorType: ERROR_TYPE_EMAIL_READ,
        errorMessage: err.message
      })
    }

    console.info('Read draft email with length', fileData.length)

    if (!graphToken) {
      graphToken = await getGraphAccessToken(userData)
    }
    const fields = await readMailMetadata(itemId, graphToken)

    dispatch(updateProgressDialog(
      'PROGRESS_STATUS_SEND_DRAFT_EMAIL'
    ))

    try {
      await sendMail(itemId, graphToken)
    } catch (err) {
      let sentAlready = false

      try {
        const latestState = await readMailSyncState(itemId, graphToken)
        sentAlready = latestState?.isDraft === false
      } catch (verifyErr) {
        // After send, message can be moved/removed from the draft id path.
        sentAlready = Number(verifyErr?.status) === 404
      }

      if (!sentAlready) {
        return dispatch({
          type: ERROR_ACTION,
          errorType: ERROR_TYPE_EMAIL_SEND,
          errorMessage: err.message
        })
      }

      console.warn('send and file: send endpoint failed, but message appears already sent; continuing with file save')
    }

    const siteUrl = getItemUrl(siteItem)
    const path = fileItem.path
    const fileName = cleanFileName(await emailFileNameOutlookComposeMode())
    console.info('upload: saving document on', siteUrl, path, fileName)
    console.info('send and file telemetry: upload start', {
      itemId,
      fileName,
      mimeBytes: fileData.length,
      elapsedMs: Date.now() - flowStartedAt
    })

    dispatch(updateProgressDialog(
      'PROGRESS_STATUS_UPLOAD_EMAIL'
    ))

    try {
      const spToken = await getSpAccessToken(userData)
      const result = await writeSharePointFile({
        siteUrl,
        path,
        fileName,
        fileData
      }, spToken)
      await updateEmailListItemFields({
        siteUrl,
        listItemUrl: result.listItemUrl,
        fields
      }, spToken)
    } catch (err) {
      return dispatch({
        type: ERROR_ACTION,
        errorType: resolveLockedFileErrorType(err, ERROR_TYPE_FILE_UPLOAD),
        errorMessage: resolveLockedFileErrorMessage(err)
      })
    }

    try {
      const meta = {
        type: siteItem.type,
        id: siteItem.id,
        path,
        fileName,
        url: encodeURI(`${siteUrl}/${path}/${fileName}`)
      }
      await api.addFileSavedEvent(meta)
    } catch (e) {
      console.warn('failed to add file saved event', e)
    }

    console.info('send and file: email saved successfully')
    dispatch(endProgressDialog())
    dispatch(showFeedback('SEND_AND_FILE_SUCCESS', '', 'success'))
    closeOutlookWindow()
  }
}

/**
 * If host extension is the same as the file extension, do nothing.
 * Otherwise append host extension to current file name.
 */
function computeFileName (fileName) {
  const hostExtension = getFileExtensionFromHost()
  if (hostExtension) {
    const fileExtension = getFileExtension(fileName)
    if (fileExtension === hostExtension) {
      return fileName
    } else {
      return `${fileName}.${hostExtension}`
    }
  } else {
    return fileName
  }
}

function isSharePointPdfConvertible (fileName) {
  const ext = getFileExtension(String(fileName || ''))
  return PDF_CONVERTIBLE_FILES.includes(ext)
}

function getPdfCopyFileName (name) {
  const fileName = String(name || '').trim()
  const ext = getFileExtension(fileName)
  if (!fileName.length || !ext.length) {
    return `${fileName || 'document'}.pdf`
  }

  const suffix = `.${ext}`
  return fileName.toLowerCase().endsWith(suffix)
    ? `${fileName.slice(0, -suffix.length)}.pdf`
    : `${fileName}.pdf`
}

function normalizeSharePointMetadataFields (metadata) {
  const source = metadata && typeof metadata === 'object' ? metadata : {}
  return Object.keys(source).reduce((acc, key) => {
    const value = source[key]
    if (typeof value === 'undefined') {
      return acc
    }
    acc[key] = value
    return acc
  }, {})
}

async function writeSharePointFileWithOverwritePrompt ({
  siteUrl,
  path,
  fileName,
  fileData,
  overwrite,
  spToken,
  onOverwriteConflict
}) {
  async function attemptWrite (overwriteFlag, retriedOverwriteConflict = false) {
    try {
      return await writeSharePointFile({
        siteUrl,
        path,
        fileName,
        fileData,
        overwrite: overwriteFlag
      }, spToken)
    } catch (err) {
      const isConflict = isSharePointNameConflictError(err)

      const canPromptOverwrite = overwriteFlag === false &&
        isConflict &&
        typeof onOverwriteConflict === 'function'

      if (canPromptOverwrite) {
        const shouldOverwrite = await onOverwriteConflict(fileName)
        if (!shouldOverwrite) {
          const aborted = new Error('User aborted overwrite confirmation')
          aborted.code = 'USER_ABORTED_OVERWRITE'
          throw aborted
        }

        return attemptWrite(true, false)
      }

      if (overwriteFlag === true && isConflict && !retriedOverwriteConflict) {
        // Equivalent-name collisions can persist even with overwrite=true; fallback to overwrite by resolved equivalent file.
        try {
          return await overwriteSharePointFileByEquivalentName({
            siteUrl,
            path,
            fileName,
            fileData
          }, spToken)
        } catch (fallbackErr) {
          // SharePoint can transiently return equivalent-name conflict right after a prior overwrite write.
          if (isSharePointNameConflictError(fallbackErr)) {
            await wait(250)
            return attemptWrite(true, true)
          }
          throw fallbackErr
        }
      }

      throw err
    }
  }

  return attemptWrite(overwrite, false)
}

function isSharePointNameConflictError (err) {
  const status = Number(err?.status)
  const knownConflictStatus = status === 400 || status === 409 || status === 500

  const details = `${String(err?.message || '')} ${String(err?.debugMessage || '')}`.toLowerCase()
  const hasConflictMessage = details.includes('equivalent name exists') ||
    details.includes('already exists') ||
    details.includes('same name') ||
    details.includes('a file with the name')

  // SharePoint code for equivalent-name collision seen in file save responses.
  const hasConflictCode = details.includes('-2130245249')

  return (knownConflictStatus && hasConflictMessage) || hasConflictCode
}

function readDroppedFileArrayBuffer (file) {
  if (file && typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer()
  }

  return new Promise((resolve, reject) => {
    try {
      const reader = new window.FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = () => reject(new Error('Failed to read dropped file data'))
      reader.readAsArrayBuffer(file)
    } catch (err) {
      reject(err)
    }
  })
}

async function waitForSharePointFileReadable (absoluteUrl, spToken) {
  if (!absoluteUrl) {
    return
  }

  const maxAttempts = 6
  const retryDelayMs = 500

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      await readSharePointFile(absoluteUrl, spToken)
      return
    } catch (err) {
      if (attempt === maxAttempts) {
        console.info('pdf copy readiness check timed out, continuing save flow', absoluteUrl)
        return
      }
      await wait(retryDelayMs)
    }
  }
}
